import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Paperclip, X, Lock, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, API, errMsg } from "@/lib/api";

const ATTACH_TYPES = ["Risk assessment", "Worksheet", "Practical instructions", "Technician notes", "Chemical safety", "Other"];

export function DocLinks({ docs, onUnlink, testId = "linked-docs" }) {
  if (!docs?.length) return null;
  return (
    <ul className="flex flex-wrap gap-2" data-testid={testId}>
      {docs.map((d) => (
        <li key={d.id} className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs" data-testid={`${testId}-item-${d.id}`}>
          <FileText className="h-3.5 w-3.5 text-primary" />
          <a href={`${API}/documents/${d.id}/file`} target="_blank" rel="noreferrer" className="font-medium hover:underline" data-testid={`${testId}-open-${d.id}`}>{d.title}</a>
          <span className="text-muted-foreground">{d.category}</span>
          {d.visibility === "prep" && <Lock className="h-3 w-3 text-muted-foreground" />}
          {onUnlink && <button onClick={() => onUnlink(d)} className="ml-0.5 text-muted-foreground hover:text-destructive" aria-label="Remove document from this item" title="Remove from this item" data-testid={`${testId}-unlink-${d.id}`}><X className="h-3.5 w-3.5" /></button>}
        </li>
      ))}
    </ul>
  );
}

function PickExisting({ practicalId, linked, onDone }) {
  const [q, setQ] = useState("");
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/documents", { params: { q } }).then((r) => setDocs(r.data.documents)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const link = async (d) => {
    try {
      const { data } = await api.post(`/documents/${d.id}/practicals`, { practical_id: practicalId });
      toast.success(`Attached ${d.title}`);
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const ids = new Set(linked.map((d) => d.id));
  return (
    <div className="min-w-0 space-y-3">
      <div className="relative"><Search className="h-4 w-4 absolute left-3 top-2.5 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources…" className="pl-9" data-testid="attach-doc-search" /></div>
      <ul className="max-h-72 w-full min-w-0 overflow-y-auto divide-y rounded-lg border">
        {docs.length === 0 && <li className="p-3 text-sm text-muted-foreground">No resources found. Upload one instead.</li>}
        {docs.map((d) => (
          <li key={d.id} className="flex min-w-0 items-center justify-between gap-3 px-3 py-2">
            <div className="min-w-0"><div className="text-sm font-medium truncate">{d.title}</div><div className="text-xs text-muted-foreground">{d.category}</div></div>
            <Button size="sm" variant="outline" className="shrink-0" disabled={ids.has(d.id)} onClick={() => link(d)} data-testid={`attach-doc-link-${d.id}`}>{ids.has(d.id) ? "Attached" : "Attach"}</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UploadNew({ practicalId, onDone }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ title: "", category: "Risk assessment", visibility: "all" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      Object.entries({ ...f, practical_id: practicalId }).forEach(([k, v]) => fd.append(k, v));
      await api.post("/documents", fd);
      const { data } = await api.get(`/documents/for-practical/${practicalId}`);
      toast.success("Uploaded and attached");
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <input ref={ref} type="file" className="hidden" accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.webp"
        onChange={(e) => { const x = e.target.files?.[0]; setFile(x || null); if (x && !f.title) setF((o) => ({ ...o, title: x.name.replace(/\.[^.]+$/, "") })); }} data-testid="attach-upload-file-input" />
      <button onClick={() => ref.current?.click()} className="w-full rounded-xl border-2 border-dashed p-5 text-sm text-muted-foreground hover:border-primary/60" data-testid="attach-upload-file-button">
        {file ? <span className="text-foreground font-medium">{file.name}</span> : "Choose a PDF, Word, Excel, text file or image (max 15 MB)"}
      </button>
      <div className="space-y-1"><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="attach-upload-title" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1"><Label>Type</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="attach-upload-category"><SelectValue /></SelectTrigger>
            <SelectContent>{ATTACH_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1"><Label>Who can see it</Label>
          <Select value={f.visibility} onValueChange={(v) => setF({ ...f, visibility: v })}><SelectTrigger data-testid="attach-upload-visibility"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All staff</SelectItem><SelectItem value="prep">Technicians and admins only</SelectItem></SelectContent></Select></div>
      </div>
      <Button onClick={save} disabled={busy || !file || !f.title.trim()} className="w-full" data-testid="attach-upload-save"><Upload className="h-4 w-4 mr-2" />{busy ? "Uploading…" : "Upload and attach"}</Button>
    </div>
  );
}

export function PracticalDocs({ practicalId, canEdit }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("existing");
  const load = useCallback(() => api.get(`/documents/for-practical/${practicalId}`).then((r) => setDocs(r.data)).catch(() => {}), [practicalId]);
  useEffect(() => { load(); }, [load]);
  const unlink = async (d) => {
    try {
      const { data } = await api.delete(`/documents/${d.id}/practicals/${practicalId}`);
      setDocs(data);
      toast.success(`Removed ${d.title} from this practical`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const done = (data) => { setDocs(data); setOpen(false); };
  const openDialog = () => { setTab("existing"); setOpen(true); };
  if (!canEdit && !docs.length) return null;
  return (
    <div data-testid="practical-docs-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold">Risk assessments & worksheets</h4>
        {canEdit && <Button size="sm" variant="outline" onClick={openDialog} data-testid="practical-attach-doc-button"><Paperclip className="h-3.5 w-3.5 mr-1.5" />Attach</Button>}
      </div>
      {docs.length ? <DocLinks docs={docs} onUnlink={canEdit ? unlink : null} testId="practical-docs" /> : <p className="text-xs text-muted-foreground" data-testid="practical-docs-empty">No documents attached yet.</p>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg overflow-hidden" data-testid="attach-doc-dialog">
          <DialogHeader><DialogTitle>Attach a document</DialogTitle></DialogHeader>
          <Tabs value={tab} onValueChange={setTab} className="min-w-0"><TabsList className="grid w-full min-w-0 grid-cols-2">
            <TabsTrigger className="min-w-0 truncate" value="existing" data-testid="attach-tab-existing">From resources</TabsTrigger>
            <TabsTrigger className="min-w-0 truncate" value="upload" data-testid="attach-tab-upload">Upload new</TabsTrigger>
          </TabsList></Tabs>
          {tab === "existing" ? <PickExisting practicalId={practicalId} linked={docs} onDone={done} /> : <UploadNew practicalId={practicalId} onDone={done} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
